import { useCallback } from 'react';

import type { accountsTypes } from 'app-components/flux/accounts';
import { accountsSelectors } from 'app-components/flux/accounts';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { useAppStore } from 'hooks/redux';
import { DateTime } from 'luxon';
import * as iHelpers from 'utils/iHelpers';
import type { TxnActionProps } from './types';

// This hook provides a callback to retrieve TxnActionProps for a given transaction. These props provide helpful
// fields and attributes to determine which txn to edit, other side of txn, txn form to use, whether txns are
// protected open a transaction details dialog for a given transaction. The returned callback
// can either be given a txn or TxnActionProps (if those are already available) and will open the appropriate dialog
// based on the txn type.
//
const useTxnActionProps = (): ((txn: TransactionRecord, options?: { isInvoiceDash?: boolean }) => TxnActionProps) => {
  const store = useAppStore();
  const state = store.getState();
  const accountsById = accountsSelectors.getAccountsById(state);
  const businessEnabled = entitlementsSelectors.getBusinessEnabled(state);
  const txnsById = transactionsSelectors.getTransactionsById(state);

  return useCallback(
    (txn: TransactionRecord, options: { isInvoiceDash?: boolean } = {}): TxnActionProps => {
      const attributes: TxnActionProps['attributes'] = {};

      const account: accountsTypes.Account | undefined = accountsById.get(txn.accountId as string);
      const matchingTransferIfTransfer = transactionsUtils.findMatchingTransferTxn(txnsById, txn);

      attributes.isInAccountsReceivable = account?.type === 'ACCOUNTS_RECEIVABLE';
      attributes.isInInvestmentAccount = account?.type === 'INVESTMENT';
      attributes.isInPaymentAccount = account?.type === 'PAYMENT';

      if (
        (txn.type !== 'INVOICE' || (txn.subtype === 'SALES_RECEIPT' && !options.isInvoiceDash)) &&
        account?.subType !== 'UNDEPOSITED_FUNDS'
      ) {
        attributes.isMatchable = true;
      } else {
        attributes.isMatchable = false;
      }

      attributes.isOtherSideInAccountsReceivable = transactionsUtils.isOtherSideInAccountsReceivable(
        accountsById,
        matchingTransferIfTransfer,
      );

      let allocations;
      if (attributes.isInAccountsReceivable) {
        allocations = txn.allocations;
      } else if (attributes.isOtherSideInAccountsReceivable) {
        allocations = matchingTransferIfTransfer?.txn.allocations;
      }
      if (allocations) {
        const allocated = iHelpers.reduce(allocations, 0.0, (sum, allocation) => sum + Number(allocation.amount ?? 0));
        attributes.hasAllocations = allocated > 0.005;
        attributes.isFullyAllocated = allocated >= Math.abs(txn.amount);
      }

      attributes.isProtectedTxn = transactionsUtils.isProtectedTxn(txn, accountsById, matchingTransferIfTransfer);

      let form: TxnActionProps['form'] = 'TRANSACTION_DETAILS';
      if (attributes.isInInvestmentAccount) form = 'INVESTMENT_TRANSACTION_DETAILS';
      else if (txn.type === 'INVOICE' || matchingTransferIfTransfer?.txn?.type === 'INVOICE') form = 'INVOICE';
      else if (attributes.isInAccountsReceivable) form = 'NONE';

      const hideActionMenu =
        attributes.isInAccountsReceivable &&
        txn.source === 'QCS_GENERATED' &&
        txn.coa?.type === 'ACCOUNT' &&
        accountsById.get(txn.coa.id)?.type === 'PAYMENT';

      let txnToEdit = txn;
      let txnOnOtherSide = matchingTransferIfTransfer?.txn;
      const isSalesTaxTxn = account?.subType === 'SALES_TAX';
      if (
        isSalesTaxTxn &&
        matchingTransferIfTransfer?.txn &&
        transactionsUtils.isSplitTxn(matchingTransferIfTransfer.txn)
      ) {
        attributes.txnToEditWasFlipped = true;
        txnToEdit = matchingTransferIfTransfer.txn;
        txnOnOtherSide = txn;
      }

      attributes.isFutureDated = txn.postedOn > DateTime.local().toISODate();
      attributes.isPersonalRefund = transactionsUtils.isRefundTxn(txn);
      attributes.isReminder =
        txn.source === 'SCHEDULED_TRANSACTION_PENDING' ||
        (transactionsUtils.isPlannedA2ATransfer(txn) &&
          txn?.matchState !== 'AUTO_MATCHED' &&
          txn?.matchState !== 'USER_MATCHED');
      attributes.isRefundIncompleteTxn = transactionsUtils.isRefundIncompleteTxn(txn);

      return {
        form,
        hideActionMenu,
        txnToEdit,
        txnOnOtherSide,
        matchingTransferIfTransfer,
        attributes,
        businessEnabled,
      };
    },
    [accountsById, businessEnabled, txnsById],
  );
};

export default useTxnActionProps;
